// Hand-authored office-complex templates.
// 'W' = wall, '.' = walkable floor, 'S' = player spawn, 'E' = enemy anchor, 'D' = exit.
// Each room is 20x20 cells, each cell = 3 world units (1.5x scale).
export const CELL_SIZE = 3;
export const ROOM_COLS = 20;
export const ROOM_ROWS = 20;

function normalizeTemplateRows(rows) {
  return rows.map((row) => {
    if (row.length < ROOM_COLS) return row.padEnd(ROOM_COLS, 'W');
    if (row.length > ROOM_COLS) return row.slice(0, ROOM_COLS);
    return row;
  });
}

const OFFICE_BASE = normalizeTemplateRows([
  'WWWWWWWWWWWWWWWWWWWW',
  'W........W.........W',
  'W........W.........W',
  'W........W..WWWWW..W',
  'W........W..W...W..W',
  'W........W..W...W..W',
  'W........W..WWWWW..W',
  'W........W.........W',
  'WWWWWWWW.W.........W',
  'W....W...D.........W',
  'W....W.............W',
  'W....W.............W',
  'W....W.....S.......W',
  'W....W.............W',
  'W....WWWWW.WWWWWW..W',
  'W..................W',
  'W......E.....E.....W',
  'W..................W',
  'W..................W',
  'WWWWWWWWWWWWWWWWWWWW',
]);

const GRAYBOX_VARIANT_A = [...OFFICE_BASE];
const GRAYBOX_VARIANT_B = OFFICE_BASE.map((row, i) => {
  if (i === 16) return 'W...E.......E......W';
  if (i === 12) return 'W....W...S.........W';
  return row;
});
const GRAYBOX_VARIANT_C = OFFICE_BASE.map((row, i) => {
  if (i === 16) return 'W.....E..E.........W';
  if (i === 12) return 'W....W.......S.....W';
  return row;
});
const GRAYBOX_VARIANT_D = OFFICE_BASE.map((row, i) => {
  if (i === 3) return 'W.....W..WWWWW.....W';
  if (i === 8) return 'WWWWWWWW...W.......W';
  if (i === 12) return 'W....W.........S...W';
  if (i === 16) return 'W..E...........E...W';
  return row;
});
const GRAYBOX_VARIANT_E = OFFICE_BASE.map((row, i) => {
  if (i === 3) return 'W........W..W.W....W';
  if (i === 9) return 'W....W...D....W....W';
  if (i === 12) return 'W....W..S..........W';
  if (i === 16) return 'W.......E.....E....W';
  return row;
});

function assertTemplateRows(name, rows) {
  const valid = rows.length === ROOM_ROWS && rows.every((row) => row.length === ROOM_COLS);
  if (!valid) throw new Error(`${name} must be ${ROOM_ROWS}x${ROOM_COLS}`);
  return rows;
}

assertTemplateRows('OFFICE_BASE', OFFICE_BASE);
assertTemplateRows('GRAYBOX_VARIANT_A', GRAYBOX_VARIANT_A);
assertTemplateRows('GRAYBOX_VARIANT_B', GRAYBOX_VARIANT_B);
assertTemplateRows('GRAYBOX_VARIANT_C', GRAYBOX_VARIANT_C);
assertTemplateRows('GRAYBOX_VARIANT_D', GRAYBOX_VARIANT_D);
assertTemplateRows('GRAYBOX_VARIANT_E', GRAYBOX_VARIANT_E);

const BOSS_W = 24;
const BOSS_H = 36;

function makeBossArenaTemplate({ finalBoss = false } = {}) {
  const rows = [];
  for (let r = 0; r < BOSS_H; r++) {
    if (r === 0 || r === BOSS_H - 1) {
      rows.push('W'.repeat(BOSS_W));
      continue;
    }
    const chars = Array(BOSS_W).fill('.');
    chars[0] = 'W';
    chars[BOSS_W - 1] = 'W';
    rows.push(chars.join(''));
  }
  // Centered south entry + spawn
  const center = Math.floor(BOSS_W / 2);
  const southEntryRow = BOSS_H - 2;
  rows[southEntryRow] = rows[southEntryRow].split('').map((ch, idx) => {
    if (idx === center) return 'D';
    return ch;
  }).join('');
  rows[BOSS_H - 4] = rows[BOSS_H - 4].split('').map((ch, idx) => (idx === center ? 'S' : ch)).join('');
  // Boss anchor on north platform center.
  rows[3] = rows[3].split('').map((ch, idx) => (idx === center ? 'B' : ch)).join('');
  // Mid-room side enemy anchors (off carpet lane).
  const enemyCols = [center - 5, center + 5];
  const enemyRows = finalBoss ? [15, 20] : [14, 19];
  for (const er of enemyRows) {
    rows[er] = rows[er].split('').map((ch, idx) => (enemyCols.includes(idx) ? 'E' : ch)).join('');
  }
  return rows;
}

const R5_BOSS = makeBossArenaTemplate({ finalBoss: false });
const R10_BOSS = makeBossArenaTemplate({ finalBoss: true });

const SANDBOX_TEST = [...OFFICE_BASE];

export const OFFICE_ROOM_REGIONS = Object.freeze([
  { id: 'lobby', kind: 'lobby', minCol: 1, maxCol: 8, minRow: 10, maxRow: 18 },
  { id: 'openOffice', kind: 'openOffice', minCol: 1, maxCol: 8, minRow: 1, maxRow: 8 },
  { id: 'privateA', kind: 'privateOffice', minCol: 13, maxCol: 16, minRow: 3, maxRow: 6 },
  { id: 'privateB', kind: 'privateOffice', minCol: 10, maxCol: 13, minRow: 10, maxRow: 14 },
  { id: 'server', kind: 'server', minCol: 14, maxCol: 18, minRow: 10, maxRow: 15 },
  { id: 'breakRoom', kind: 'break', minCol: 14, maxCol: 18, minRow: 1, maxRow: 8 },
  { id: 'bathroomCorridor', kind: 'bathroomCorridor', minCol: 9, maxCol: 10, minRow: 1, maxRow: 8 },
  { id: 'hallwayMain', kind: 'hallway', minCol: 9, maxCol: 12, minRow: 9, maxRow: 18 },
]);

export const OFFICE_THRESHOLDS = Object.freeze([
  { col: 8, row: 12 },
  { col: 9, row: 12 },
  { col: 10, row: 12 },
  { col: 11, row: 9 },
  { col: 12, row: 9 },
  { col: 12, row: 4 },
  { col: 13, row: 4 },
  { col: 13, row: 12 },
  { col: 13, row: 13 },
  { col: 13, row: 14 },
]);

export const OFFICE_CHAIR_LAYOUT = Object.freeze([
  { col: 2, row: 3, rotY: Math.PI, variant: 0, scale: 0.95 },
  { col: 3, row: 3, rotY: Math.PI, variant: 1, scale: 0.95 },
  { col: 2, row: 6, rotY: Math.PI * 0.9, variant: 2, scale: 0.92 },
  { col: 3, row: 6, rotY: Math.PI * 1.1, variant: 0, scale: 0.96 },
  { col: 6, row: 3, rotY: Math.PI, variant: 1, scale: 0.95 },
  { col: 14, row: 6, rotY: Math.PI * 0.5, variant: 2, scale: 0.9 },
  { col: 11, row: 13, rotY: Math.PI * 1.5, variant: 0, scale: 0.92 },
  { col: 16, row: 3, rotY: Math.PI * 0.75, variant: 1, scale: 0.88 },
  { col: 16, row: 5, rotY: Math.PI * 1.25, variant: 2, scale: 0.88 },
  { col: 5, row: 11, rotY: Math.PI * 0.25, variant: 0, scale: 0.95 },
  { col: 2, row: 14, rotY: Math.PI * 0.2, variant: 1, scale: 0.95 },
  { col: 2, row: 16, rotY: Math.PI * 1.8, variant: 2, scale: 0.95 },
]);

export const GRAYBOX_VARIANTS = Object.freeze({
  A: GRAYBOX_VARIANT_A,
  B: GRAYBOX_VARIANT_B,
  C: GRAYBOX_VARIANT_C,
  D: GRAYBOX_VARIANT_D,
  E: GRAYBOX_VARIANT_E,
});
export const GRAYBOX_VARIANT_ORDER = Object.freeze(['A', 'B', 'C', 'D', 'E']);
export const ROOM_TEMPLATES = [GRAYBOX_VARIANT_A, GRAYBOX_VARIANT_B, GRAYBOX_VARIANT_C, GRAYBOX_VARIANT_D, GRAYBOX_VARIANT_E];
export const BOSS_TEMPLATE_5 = R5_BOSS;
export const BOSS_TEMPLATE_10 = R10_BOSS;
export const SANDBOX_TEMPLATE = SANDBOX_TEST;
